import React, { useEffect, useState } from "react";
import { can, canUseOperatorTools } from "../../../../utils/userPrefs/validatePermissions";
import { useNotifications } from "../../../../context/NotificationContext";
import { Alert, Button, Input, Modal, Space, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, PrinterOutlined, SearchOutlined, ExclamationCircleOutlined, PaperClipOutlined, DeleteOutlined, StopOutlined, CheckOutlined, BarChartOutlined, UserAddOutlined, DownloadOutlined, ReloadOutlined } from "@ant-design/icons";

import { fetchTests, deleteTest, cancelTest, markTestAsReviewed, assignTestToPatient } from "../../../../api/healthcare/Testing/TestApi";
import { bloodTestFromOrder } from "../../../../utils/geneCodes/bloodTestShape";
import CreateTestModal from "../../results/CreateTestOrderModal";
import ResultUploadModal from "./submodals/ResultUploadModal";
import PrintOrderModal from "../../results/PrintOrderModal";
import BloodTestHistoryModal from "../../../gene.codes/bloods/BloodTestHistoryModal";
import AssignPractitionerToSampleModal from "./submodals/AssignPractitionerToSampleModal";
import { Patient } from "../../../../types/healthcare/PatientTypes";
import { Test } from "../../../../types/healthcare/TestTypes";
import { downloadBloodTestReport } from "../../../../api/file/fileServiceApi";
import DataTable from "../../../../components/shared/DataTable";
import StatusTag from "../../../../components/shared/StatusTag";
import { countAbnormalResults, getTestDisplayLabel } from "../../../../utils/healthcare/testDisplayUtils";
import { isGeneticAnalysisTest } from "../../../../utils/healthcare/geneticTest";
import { useAnalysisPolling } from "../../../../hooks/useAnalysisPolling";
import { RADIUS } from "../../../../design-system/tokens";

const { Text } = Typography;
const { confirm } = Modal;

interface TestingTabProps {
	patient: Patient;
}

const TestingTab: React.FC<TestingTabProps> = ({ patient }) => {
	const [orders, setOrders] = useState<Test[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [dialogVisible, setDialogVisible] = useState<boolean>(false);
	const [currentOrder, setCurrentOrder] = useState<Partial<Test> | null>(null);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [printModalVisible, setPrintModalVisible] = useState<boolean>(false);
	const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
	const [resultUploadVisible, setResultUploadVisible] = useState<boolean>(false);
	const [selectedTestForResults, setSelectedTestForResults] = useState<Test | null>(null);
	const [filterValue, setFilterValue] = useState<string>("");
	const [bloodHistoryVisible, setBloodHistoryVisible] = useState<boolean>(false);
	const [assignTestVisible, setAssignTestVisible] = useState<boolean>(false);
	const [assignTestSampleId, setAssignTestSampleId] = useState<string>("");
	const [assignPractitionerModalVisible, setAssignPractitionerModalVisible] = useState(false);
	const [selectedSampleForPractitioner, setSelectedSampleForPractitioner] = useState<string>("");

	const { showError, showSuccess, showInfo } = useNotifications();

	useEffect(() => {
		if (patient?.id) {
			loadTestsData();
		}
	}, [patient]);

	// `silent` is the background-poll mode (86d3vhb3j S6): no table spinner and no error
	// toast, because a spinner flashing every 20s — or a toast on one transient failure —
	// is worse than the stale row the next tick will fix.
	const loadTestsData = async (options?: { silent?: boolean }) => {
		if (!patient?.id) return;
		const silent = options?.silent === true;
		if (!silent) setLoading(true);
		try {
			setOrders(await fetchTests(patient.id));
		} catch {
			if (!silent) showError("Failed to load test orders");
		} finally {
			if (!silent) setLoading(false);
		}
	};

	// 86d3vhb3j S6 — reload the list while any order is still being analysed so it flips
	// to Results Available / Analysis Failed without the practitioner reloading the page.
	// `analysis_failed` is terminal and is not polled.
	const { exhausted: analysisPollExhausted } = useAnalysisPolling({
		active: orders.some((order) => order.status === "in_progress"),
		onPoll: () => loadTestsData({ silent: true }),
	});

	const openNewOrderDialog = () => {
		setCurrentOrder({ patient_id: patient.id, priority: "routine", status: "ordered", details: {} });
		setIsEditing(false);
		setDialogVisible(true);
	};

	const handleDeleteTest = (order: Test) => {
		confirm({
			title: "Confirm Deletion",
			icon: <ExclamationCircleOutlined />,
			content: "Are you sure you want to delete this test order? This action cannot be undone.",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await deleteTest(order.id);
					showSuccess("Test order deleted successfully");
					await loadTestsData();
				} catch {
					showError("Failed to delete test order");
				}
			},
		});
	};

	const handleCancelTest = (order: Test) => {
		confirm({
			title: "Confirm Cancellation",
			icon: <ExclamationCircleOutlined />,
			content: "Are you sure you want to cancel this test order?",
			okText: "Cancel Order",
			okType: "danger",
			cancelText: "Keep",
			onOk: async () => {
				try {
					await cancelTest(order.id, "Cancelled by provider");
					showSuccess("Test order cancelled successfully");
					await loadTestsData();
				} catch {
					showError("Failed to cancel test order");
				}
			},
		});
	};

	const handleMarkAsReviewed = async (testId: number) => {
		try {
			await markTestAsReviewed(testId);
			showSuccess("Test marked as reviewed");
			await loadTestsData();
		} catch {
			showError("Failed to mark test as reviewed");
		}
	};

	const handleSaveTest = async () => {
		showSuccess(isEditing ? "Test order updated successfully" : "Test order created successfully");
		setDialogVisible(false);
		await loadTestsData();
	};

	const handleAssignTestToPatient = async () => {
		if (!assignTestSampleId.trim()) {
			showError("Please enter a sample ID");
			return;
		}
		try {
			setLoading(true);
			const result = await assignTestToPatient(assignTestSampleId, patient.id);
			showSuccess(`Test order successfully assigned to ${patient.first_name} ${patient.last_name}. Original patient ID: ${result.original_patient_id}`);
			setAssignTestVisible(false);
			setAssignTestSampleId("");
			await loadTestsData();
		} catch (error: any) {
			showError(error?.response?.data?.message || "Failed to assign test order");
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadReport = async (fileId: string) => {
		try {
			await downloadBloodTestReport(patient.id, fileId);
		} catch {
			showError("Failed to download report file");
		}
	};

	const filteredOrders = orders.filter((order) => filterValue === "" || [order.test_category, order.status, order.priority].some((v) => v?.toLowerCase().includes(filterValue.toLowerCase())));

	const bloodTestsAvailable = orders.some((o) => (bloodTestFromOrder(o)?.results?.length ?? 0) > 0);

	const columns: ColumnsType<Test> = [
		// { title: "SampleID", dataIndex: "sample_id", key: "sample_id", render: (v) => v || "Undefined" },
		{
			title: "Test",
			dataIndex: "test_type",
			key: "test",
			// Use the shared label helper so an order reads the same here as in the
			// Overview "Recent Tests" list (which is test_name-based) — never two ways.
			render: (_val, record) => getTestDisplayLabel(record),
		},
		{ title: "Date", dataIndex: "test_date", key: "date", render: (_v, record) => { const d = record.test_date || record.created_at; return d ? new Date(d).toLocaleDateString() : "Unknown"; }, sorter: (a, b) => new Date(a.test_date || a.created_at || 0).getTime() - new Date(b.test_date || b.created_at || 0).getTime(), defaultSortOrder: "descend" as const, width: 140 },
		{ title: "Status", dataIndex: "status", key: "status", render: (v) => <StatusTag status={v} />, width: 160 },
		{
			title: "Alerts",
			key: "alerts",
			width: 80,
			align: "center" as const,
			render: (_: any, record: Test) => {
				const count = countAbnormalResults(record);
				if (!count) return null;
				return (
					<Tooltip title={`${count} result${count > 1 ? "s" : ""} outside reference range`}>
						<span
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 4,
								padding: "2px 8px",
								borderRadius: RADIUS.xs,
								background: "var(--mg-danger-bg)",
								border: "1px solid var(--mg-danger)",
								color: "var(--mg-danger)",
								fontSize: 11,
								fontWeight: 600,
							}}
						>
							<ExclamationCircleOutlined style={{ fontSize: 11 }} />
							{count}
						</span>
					</Tooltip>
				);
			},
		},
		{
			title: "Actions",
			key: "actions",
			width: 200,
			render: (_: any, record) => (
				<Space>
					<Button
						type="text"
						icon={<PrinterOutlined />}
						onClick={(e) => {
							e.stopPropagation();
							setSelectedOrderIds([record.id]);
							setPrintModalVisible(true);
						}}
					/>
					{record.sample_id && isGeneticAnalysisTest(record) && (
						<Button
							type="text"
							icon={<UserAddOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								setSelectedSampleForPractitioner(record.sample_id);
								setAssignPractitionerModalVisible(true);
							}}
						/>
					)}
					{/* Healthcare only ever writes snake_case statuses — the camelCase
					    `resultsAvailable` this used to test for never occurs, so this
					    guard was evaluated against a value that cannot exist and the
					    mark-reviewed button below never rendered for anyone. */}
					{!["completed", "cancelled", "results_available"].includes(record.status || "") && (
						<Button
							type="text"
							icon={<PaperClipOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								setSelectedTestForResults(record);
								setResultUploadVisible(true);
							}}
						/>
					)}
					{record.status === "results_available" && (
						<Tooltip title="Mark results reviewed">
							<Button
								type="text"
								icon={<CheckOutlined />}
								data-testid="testing-mark-reviewed"
								onClick={(e) => {
									e.stopPropagation();
									handleMarkAsReviewed(record.id);
								}}
							/>
						</Tooltip>
					)}
					{["ordered", "scheduled"].includes(record.status || "") && (
						<Button
							type="text"
							icon={<StopOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								handleCancelTest(record);
							}}
						/>
					)}
					<Button
						type="text"
						danger
						icon={<DeleteOutlined />}
						onClick={(e) => {
							e.stopPropagation();
							handleDeleteTest(record);
						}}
					/>
					{record.report_file_id && (
						<Button
							type="text"
							icon={<DownloadOutlined />}
							title="Download original report"
							onClick={(e) => {
								e.stopPropagation();
								handleDownloadReport(record.report_file_id!);
							}}
						/>
					)}
				</Space>
			),
		},
	];

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
				<Space>
					<Button type="primary" icon={<PlusOutlined />} onClick={openNewOrderDialog}>
						New Test Order
					</Button>
					{canUseOperatorTools() && can("healthcare.test_orders.write") && (
						<Button
							icon={<UserAddOutlined />}
							onClick={() => {
								setAssignTestSampleId("");
								setAssignTestVisible(true);
							}}
						>
							Assign Existing Test
						</Button>
					)}
					{bloodTestsAvailable && (
						<Button icon={<BarChartOutlined />} onClick={() => setBloodHistoryVisible(true)}>
							Blood Test History
						</Button>
					)}
				</Space>
				<Input placeholder="Search tests..." value={filterValue} onChange={(e) => setFilterValue(e.target.value)} prefix={<SearchOutlined />} style={{ minWidth: 240 }} allowClear />
			</div>

			{analysisPollExhausted && (
				<Alert
					type="info"
					showIcon
					message="Still analysing"
					description="A test order has been analysing for a while, so auto-refresh has stopped. Refresh to check again."
					action={
						<Button size="small" icon={<ReloadOutlined />} onClick={() => loadTestsData()}>
							Refresh
						</Button>
					}
				/>
			)}

			<DataTable
				columns={columns}
				dataSource={filteredOrders}
				rowKey={(r) => String(r.id)}
				loading={loading}
				onRowClick={(record) => {
					setCurrentOrder({ ...record, patient });
					setIsEditing(true);
					setDialogVisible(true);
				}}
				emptyTitle="No test orders found"
				emptyDescription="Create a new test order to get started"
				emptyAction={{ label: "New Test Order", onClick: openNewOrderDialog }}
			/>

			{dialogVisible && currentOrder && (
				<CreateTestModal isOpen={dialogVisible} onClose={() => setDialogVisible(false)} onSave={handleSaveTest} currentOrder={currentOrder} setCurrentOrder={setCurrentOrder} patient={patient} allTests={orders} />
			)}
			{resultUploadVisible && selectedTestForResults && (
				<ResultUploadModal
					isOpen={resultUploadVisible}
					onClose={async () => {
						setResultUploadVisible(false);
						setSelectedTestForResults(null);
						await loadTestsData();
					}}
					test={selectedTestForResults}
				/>
			)}
			{printModalVisible && (
				<PrintOrderModal
					isOpen={printModalVisible}
					onClose={() => {
						setPrintModalVisible(false);
						setSelectedOrderIds([]);
					}}
					orderIds={selectedOrderIds}
					title={`Print ${selectedOrderIds.length} Test Order${selectedOrderIds.length !== 1 ? "s" : ""}`}
				/>
			)}
			{bloodHistoryVisible && <BloodTestHistoryModal isOpen={bloodHistoryVisible} onClose={() => setBloodHistoryVisible(false)} allTests={orders} patient={patient} />}
			{assignPractitionerModalVisible && selectedSampleForPractitioner && (
				<AssignPractitionerToSampleModal
					visible={assignPractitionerModalVisible}
					onClose={() => {
						setAssignPractitionerModalVisible(false);
						setSelectedSampleForPractitioner("");
					}}
					patient={patient}
					sampleId={selectedSampleForPractitioner}
				/>
			)}
			<Modal
				title="Assign Existing Test Order"
				open={assignTestVisible}
				onOk={handleAssignTestToPatient}
				onCancel={() => {
					setAssignTestVisible(false);
					setAssignTestSampleId("");
				}}
				confirmLoading={loading}
				okText="Assign Test"
				destroyOnClose
			>
				<div style={{ marginBottom: 16 }}>
					<Text style={{ display: "block", marginBottom: 8 }}>
						Search for an existing test order by Sample ID and assign it to{" "}
						<Text strong>
							{patient.first_name} {patient.last_name}
						</Text>
						.
					</Text>
					<Text type="secondary" style={{ fontSize: 13 }}>
						This will create a copy of the existing test order for this patient.
					</Text>
				</div>
				<Input placeholder="Enter Sample ID" value={assignTestSampleId} onChange={(e) => setAssignTestSampleId(e.target.value)} onPressEnter={handleAssignTestToPatient} autoFocus />
			</Modal>
		</div>
	);
};

export default TestingTab;
